/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { Button } from '@/components/button'
import { Calendar, Checkmark, Close, FilterNoBg } from '@/components/icons'
import { ImportMatchesModal } from '@/components/modal'
import { Search } from '@/components/search'
import { MatchItemLoading } from '@/components/skeleton'
import { CLUB_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { ROUTE_CLUBS } from '@/constants/Routes'
import { useDebounce } from '@/hooks/useDebounce'
import { useOutsideClick } from '@/hooks/useOutsideClick'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useGetClubMatches, useSearchClubMembers } from '@/services'
import { ClubMember, Match, Order } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import moment from 'moment'
import { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { clubSelector } from '../selectors'
import { ClubMatchItem } from './ClubMatchItem'

export const ClubMatches = () => {
  const { t } = useTranslation()

  const params = useParams()
  const navigate = useNavigate()
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const { clubId } = params

  const { mutate: getClubMatches, data: resClubMatches, isLoading } = useGetClubMatches()

  const [offset, setOffset] = useState(OFFSET)
  const [clubMatches, setClubMatches] = useState<Match[]>([])
  const [order, setOrder] = useState<Order>('DESC')
  const [eventFormat, setEventFormat] = useState<string[]>([])
  const [openImport, setOpenImport] = useState(false)
  const [lastElement, setLastElement] = useState<any>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [searchEvent, setSearchEvent] = useState('')
  const [searchPlayer, setSearchPlayer] = useState<{ name: string; id: string; isOpen?: boolean }>({ name: '', id: '' })

  const debouncedEventName = useDebounce<string>(searchEvent || '', 200)
  const debouncedPlayerName = useDebounce<string>(searchPlayer.name || '', 200)

  const { data: resultSearchClubMembers } = useSearchClubMembers({
    clubId: Number(clubId),
    exclude: [],
    filter: {
      lat: user?.addresses?.[0]?.latitude || null,
      lng: user?.addresses?.[0]?.longitude || null,
    },
    includePendingPlayers: true,
    includeStaff: true,
    includeUnclaimedPlayers: true,
    limit: 20,
    offset: OFFSET,
    query: debouncedPlayerName ? debouncedPlayerName : '*',
  })

  console.log({ resultSearchClubMembers })

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const toggleOpenImport = () => {
    setOpenImport(!openImport)
  }

  const handleClickOutside = () => {
    setSearchPlayer({ ...searchPlayer, isOpen: false })
  }

  const ref = useOutsideClick(handleClickOutside)

  const onChangeEventFormat = (type: string) => {
    if (eventFormat.includes(type)) {
      const filterArray = eventFormat.filter((item) => item !== type)
      setEventFormat(filterArray)
    }
    if (!eventFormat.includes(type)) setEventFormat([...eventFormat, type])
    setClubMatches([])
    setOffset(OFFSET)
  }

  const onChangeOrder = (type: Order) => {
    if (order !== type || offset !== OFFSET) {
      setOrder(type)
      setClubMatches([])
      setOffset(OFFSET)
    }
  }

  const onClearFilter = () => {
    setOrder('DESC')
    setEventFormat([])
    setClubMatches([])
    setOffset(OFFSET)
    setStartDate(null)
    setEndDate(null)
    setSearchEvent('')
    setSearchPlayer({ name: '', id: '' })
  }

  const onNavigateMatchNew = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}/match/new`)
  }

  const onGetData = () => {
    if (clubId)
      getClubMatches({
        clubId: Number(clubId),
        filters: {
          ...(eventFormat.length && { eventFormat: eventFormat }),
          ...(debouncedEventName && { eventName: debouncedEventName }),
          ...(searchPlayer.id && { playerId: Number(searchPlayer.id) }),
          ...(startDate &&
            endDate && {
              eventDate: {
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate: moment(endDate).format('YYYY-MM-DD'),
              },
            }),
        },
        limit: LIMIT,
        offset: offset,
        sort: {
          order: order,
          parameter: 'MATCH_DATE',
        },
      })
  }

  const onChangeDateRange = (dates: any) => {
    setClubMatches([])
    setOffset(OFFSET)
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  const onSearchEventName = (e: ChangeEvent<HTMLInputElement>) => {
    setClubMatches([])
    setOffset(OFFSET)
    setSearchEvent(e.target.value)
  }

  const onSearchPlayer = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchPlayer({
      id: '',
      name: e.target.value,
      isOpen: true,
    })
  }

  const onSelectPlayer = (clubMember: ClubMember) => {
    setClubMatches([])
    setOffset(OFFSET)
    setSearchPlayer({
      id: clubMember?.id.toString(),
      name: clubMember?.fullName,
    })
  }

  useEffect(() => {
    onGetData()
  }, [clubId, offset, order, eventFormat, startDate, endDate, debouncedEventName, searchPlayer.id])

  useEffect(() => {
    if (resClubMatches) setClubMatches([...clubMatches, ...resClubMatches.result.hits])
  }, [resClubMatches])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <>
      <div className="flex flex-col gap-y-3">
        <div className="flex justify-between">
          <h4 className="text-xl text-white font-semibold">{t('common.matches')}</h4>
          {has(permissions, CLUB_MODIFY) ? (
            <Menu as="div" className="relative inline-block">
              <div>
                <Menu.Button className="py-2 px-4 rounded-full bg-white bg-opacity-10 text-sm font-semibold text-white">
                  {t('common.addMatches')}
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute z-50 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-1 py-1 ">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm gap-x-1`}
                          onClick={onNavigateMatchNew}
                        >
                          {t('common.addMatches')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          onClick={toggleOpenImport}
                        >
                          {t('club.importMatchesCSV')}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : null}
        </div>

        <div className="flex gap-x-2 sm:gap-x-4 items-center justify-between sm:justify-start">
          <div className="flex gap-x-2 sm:gap-x-4 items-center">
            <Menu as="div" className="relative inline-block">
              <div>
                <Menu.Button className="flex gap-x-1 items-center bg-primary-500 rounded-full hover:bg-opacity-70 px-3 py-1 text-xs font-semibold text-white">
                  {t('common.sort')}
                  <FilterNoBg className="w-4 h-4" />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute mt-2 z-20 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-1 py-1 ">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm gap-x-1`}
                          onClick={() => onChangeOrder('DESC')}
                        >
                          <Checkmark className={`${order === 'DESC' ? 'opacity-100' : 'opacity-0'}`} />
                          {t('common.newToOld')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          onClick={() => onChangeOrder('ASC')}
                        >
                          <Checkmark className={`${order === 'ASC' ? 'opacity-100' : 'opacity-0'}`} />
                          {t('common.oldToNew')}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            <Button
              className="text-xs font-semibold text-white border border-primary-500 bg-primary-500 bg-opacity-5 px-2 sm:px-4 py-1 flex flex-row gap-x-1 items-center"
              onClick={() => onChangeEventFormat('SINGLES')}
            >
              {eventFormat.includes('SINGLES') && <Checkmark className="w-4 h-4 text-white" />}
              {t('common.singles')}
            </Button>
            <Button
              className="text-xs font-semibold text-white border border-primary-500 bg-primary-500 bg-opacity-5 px-2 sm:px-4 py-1 flex flex-row gap-x-1 items-center"
              onClick={() => onChangeEventFormat('DOUBLES')}
            >
              {eventFormat.includes('DOUBLES') && <Checkmark className="w-4 h-4 text-white" />}
              {t('common.doubles')}
            </Button>
          </div>
          <div className="flex items-center">
            <Button
              className="text-xs text-white hidden sm:flex font-semibold hover:text-opacity-70"
              onClick={onClearFilter}
            >
              {t('common.clearFilters')}
            </Button>
            <Button className="flex sm:hidden" onClick={onClearFilter}>
              <Close className="text-white hover:text-opacity-70" />
            </Button>
          </div>
        </div>

        {has(permissions, CLUB_MODIFY) && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Calendar
                className={`absolute top-1/2 -translate-y-1/2 left-4 z-10 pointer-events-none text-navy-900 w-5 h-5`}
              />
              <ReactDatePicker
                popperClassName="z-20"
                popperPlacement="top-start"
                className="px-4 pl-12 py-2 rounded-lg text-sm font-semibold bg-white text-navy-900 focus:outline-none max-w-[256px]"
                selectsRange
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={onChangeDateRange}
              />
            </div>

            <Search
              searchText={searchEvent}
              onChangeInput={onSearchEventName}
              className="py-2 px-4 w-52 gap-x-2 !rounded-lg"
              placeholder={t('common.eventName')}
            />

            <div className="relative" ref={ref}>
              <Search
                searchText={searchPlayer?.name || ''}
                onChangeInput={onSearchPlayer}
                className="py-2 px-4 w-52 gap-x-2 !rounded-lg"
                placeholder={t('common.playerName')}
              />
              <div
                className={`${
                  searchPlayer.isOpen ? 'h-60 overflow-y-auto' : 'h-0 opacity-0 overflow-hidden'
                } w-full mt-10 rounded-lg absolute z-50 top-0 right-0 transition-all duration-300 ease-in-out bg-white py-2 shadow-2xl`}
              >
                {resultSearchClubMembers?.result?.hits?.map((item, index) => {
                  return (
                    <div
                      className={`flex items-center justify-between px-2 ${
                        index + 1 !== resultSearchClubMembers.result.total && 'border-b'
                      } py-2 hover:bg-gray-200 hover:cursor-pointer`}
                      onClick={() => onSelectPlayer(item)}
                    >
                      <div className="flex items-center gap-x-2">
                        <img src={item?.imageUrl} className="w-6 h-6 rounded-full" />
                        <p className="text-navy-900 text-xs font-semibold">{item?.fullName}</p>
                      </div>
                      <p className="text-xs font-semibold text-gray-400">{item?.duprId}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-y-2">
          {clubMatches.map((item, index) => {
            return index === clubMatches.length - 1 && resClubMatches?.result.hasMore ? (
              <div key={item.id} ref={setLastElement}>
                <ClubMatchItem
                  match={item}
                  callbackFetch={() => {
                    setClubMatches([])
                    setOffset(OFFSET)
                    onGetData()
                  }}
                />
              </div>
            ) : (
              <ClubMatchItem
                key={item.id}
                match={item}
                callbackFetch={() => {
                  setClubMatches([])
                  setOffset(OFFSET)
                  onGetData()
                }}
              />
            )
          })}

          {isLoading && (
            <>
              <MatchItemLoading />
              <MatchItemLoading />
              <MatchItemLoading />
            </>
          )}

          {resClubMatches && !resClubMatches?.result?.hasMore && (
            <div className="flex justify-center">
              <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
            </div>
          )}
        </div>
      </div>
      {clubId && (
        <ImportMatchesModal
          open={openImport}
          onClose={toggleOpenImport}
          clubId={clubId}
          onRefresh={() => {
            setClubMatches([])
            onGetData()
          }}
        />
      )}
    </>
  )
}
